import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiFetch, API_BASE } from '~/lib/api';

interface DraftApplication {
  _id: string;
  email_subject: string;
  email_body: string;
  match_reason?: string;
  status: string;
  created_at: string;
  job?: {
    title: string;
    company: string;
    location?: string;
    wage_from?: number;
    wage_to?: number;
    wage_per?: string;
  };
}

interface SenderInfo {
  send_channel: 'gmail' | 'domain';
  sender_address: string | null;
  reply_to: string;
  forward_replies: boolean;
}

interface DashboardData {
  membership: {
    email: string;
    name?: string;
    status: string;
    daily_limit: number;
    auto_send: boolean;
    subscription_plan?: string;
    subscription_active?: boolean;
    subscription_expires_at?: string;
  };
  stats: {
    today: number;
    pending: number;
    replied: number;
    interviews: number;
    drafts: number;
    remaining_today: number;
  };
  isWeekend: boolean;
  recentApplications: Array<{
    _id: string;
    email_subject: string;
    match_reason?: string;
    status: string;
    created_at: string;
  }>;
  drafts: DraftApplication[];
}

const STATUS_BADGES: Record<string, string> = {
  DRAFT: 'badge-warning',
  QUEUED: 'badge-info',
  SENT: 'badge-info',
  REPLIED: 'badge-success',
  INTERVIEW: 'badge-accent',
  REJECTED: 'badge-error',
  NO_RESPONSE: 'badge-ghost',
};

export default function JobApplyDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [sender, setSender] = useState<SenderInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [generatingDrafts, setGeneratingDrafts] = useState(false);
  const [paymentPending, setPaymentPending] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const draftGenTriggered = useRef(false);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const statusRes = await apiFetch('/job-apply/api/onboarding-status');
      const status = await statusRes.json();
      // Connected means "we can send": own Gmail, or the address TBit manages.
      const connected = status.has_email_channel ?? status.has_gmail;
      if (!connected) {
        navigate('/job-apply/onboarding', { replace: true });
        return;
      }
      setSender({
        send_channel: status.send_channel === 'domain' ? 'domain' : 'gmail',
        sender_address: status.sender_address ?? null,
        reply_to: status.reply_to,
        forward_replies: status.forward_replies !== false,
      });
      const dashRes = await apiFetch('/job-apply/api/dashboard');
      const d = await dashRes.json();
      if (d) setData(d);
      return d;
    } catch {
      navigate('/job-apply/onboarding', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchDashboard().then((d) => {
      // Auto-generate drafts if auto_send is off and no drafts yet
      if (d && !d.membership.auto_send && d.drafts.length === 0 && !draftGenTriggered.current) {
        draftGenTriggered.current = true;
        triggerDraftGeneration();
      }
    });
  }, [fetchDashboard]);

  // Handle ?payment=success return from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      setPaymentPending(true);
      const poll = setInterval(async () => {
        try {
          const res = await apiFetch('/job-apply/api/dashboard');
          const d = await res.json();
          if (d?.membership?.subscription_active) {
            setData(d);
            setPaymentPending(false);
            clearInterval(poll);
            window.history.replaceState({}, '', '/job-apply/dashboard');
          }
        } catch { /* ignore */ }
      }, 2000);
      const timeout = setTimeout(() => {
        clearInterval(poll);
        setPaymentPending(false);
        setCheckoutError('Pago recibido. Si no ves tu plan activo, recarga la pagina.');
      }, 30000);
      return () => { clearInterval(poll); clearTimeout(timeout); };
    }
    if (params.get('payment') === 'cancelled') {
      window.history.replaceState({}, '', '/job-apply/dashboard');
    }
  }, []);

  const triggerDraftGeneration = async () => {
    setGeneratingDrafts(true);
    try {
      const res = await apiFetch('/job-apply/api/generate-drafts', { method: 'POST' });
      if (res.ok) {
        await fetchDashboard();
      }
    } catch (err) {
      console.error('Draft generation failed:', err);
    } finally {
      setGeneratingDrafts(false);
    }
  };

  const handleActivateAutoSend = async () => {
    setActivating(true);
    try {
      const res = await apiFetch('/job-apply/api/activate-auto-send', { method: 'POST' });
      if (res.ok) {
        await fetchDashboard();
      }
    } catch (err) {
      console.error('Activation failed:', err);
    } finally {
      setActivating(false);
    }
  };

  const handleCheckout = async () => {
    setCheckoutError('');
    try {
      const res = await apiFetch('/job-apply/api/create-checkout', { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Error inesperado' }));
        setCheckoutError(err.error || 'No se pudo iniciar el pago');
        return;
      }
      const { url } = await res.json();
      if (url) window.location.href = url;
    } catch {
      setCheckoutError('Error de conexion. Intenta de nuevo.');
    }
  };

  if (loading)
    return (
      <div className="flex justify-center py-12">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );

  const stats = data?.stats ?? { today: 0, pending: 0, replied: 0, interviews: 0, drafts: 0, remaining_today: 0 };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Panel</h1>
        <div className="flex items-center gap-1">
          {sender?.send_channel !== 'domain' && (
            <a href={`${API_BASE}/job-apply/auth/start`} className="btn btn-ghost btn-sm gap-1">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
              Reconectar Gmail
            </a>
          )}
          <Link to="/job-apply/onboarding?step=cv" className="btn btn-ghost btn-sm gap-1">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
            </svg>
            Cambiar CV
          </Link>
        </div>
      </div>

      {/* Managed sending address — only for applicants who did not connect Gmail */}
      {sender?.send_channel === 'domain' && (
        <div className="card bg-base-100 shadow">
          <div className="card-body py-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-primary mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
              </svg>
              <div className="space-y-1">
                {sender.sender_address ? (
                  <p className="text-sm">
                    Tus postulaciones salen de{' '}
                    <span className="font-semibold break-all">{sender.sender_address}</span>
                  </p>
                ) : (
                  <p className="text-sm">
                    Estamos preparando la direccion desde la que saldran tus postulaciones.
                  </p>
                )}
                <p className="text-sm text-base-content/70">
                  {sender.forward_replies
                    ? <>Cuando una empresa responda, te reenviamos el correo a <span className="font-medium break-all">{sender.reply_to}</span>.</>
                    : <>El reenvio de respuestas a tu correo esta desactivado. Escribenos si quieres volver a activarlo.</>}
                </p>
                <p className="text-xs text-base-content/50">
                  No necesitas revisar otra bandeja de entrada ni conectar tu cuenta de Google.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Checkout error */}
      {checkoutError && (
        <div className="alert alert-warning text-sm">
          <span>{checkoutError}</span>
        </div>
      )}

      {/* Payment processing indicator */}
      {paymentPending && (
        <div className="alert bg-primary/10 text-sm">
          <span className="loading loading-spinner loading-sm" />
          <span>Procesando tu pago... esto puede tomar unos segundos.</span>
        </div>
      )}

      {/* Subscription banner — 4 states */}
      {data && !paymentPending && (
        <>
          {/* State: active */}
          {data.membership.subscription_active && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm bg-success/10 text-success">
              <div className="w-2 h-2 rounded-full shrink-0 bg-success" />
              <span>
                Plan <strong>{data.membership.subscription_plan}</strong> activo
                {data.membership.subscription_expires_at && (
                  <> — vence {new Date(data.membership.subscription_expires_at).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })}</>
                )}
                {' '}&middot; Enviando hasta {data.membership.daily_limit} postulaciones por dia laborable
              </span>
            </div>
          )}

          {/* State: expired */}
          {!data.membership.subscription_active && data.membership.subscription_expires_at && data.membership.auto_send && (
            <div className="card bg-warning/10 border border-warning/30">
              <div className="card-body py-4">
                <p className="text-sm">
                  <strong>Tu plan PRO vencio</strong> el {new Date(data.membership.subscription_expires_at).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })}.
                  Renueva para seguir enviando postulaciones automaticamente.
                </p>
                <button className="btn btn-primary btn-sm w-fit mt-2" onClick={handleCheckout}>
                  Renovar Plan PRO — $160 USD
                </button>
              </div>
            </div>
          )}

          {/* State: free_sent (auto_send on, no subscription, no expiry = never had one) */}
          {data.membership.auto_send && !data.membership.subscription_active && !data.membership.subscription_expires_at && (
            <div className="card bg-primary/5 border border-primary/20">
              <div className="card-body">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="font-bold text-lg">Tus 3 postulaciones de prueba fueron enviadas</h3>
                </div>
                <p className="text-sm text-base-content/70 mb-4">
                  Para seguir enviando postulaciones automaticamente cada dia, activa tu plan PRO.
                </p>

                <div className="bg-base-100 rounded-lg p-4 space-y-2 mb-4">
                  <p className="font-semibold">Plan PRO — Postulaciones Automaticas</p>
                  <ul className="text-sm space-y-1 text-base-content/80">
                    <li>Hasta 20 postulaciones enviadas por dia laborable</li>
                    <li>CV y carta personalizada para cada oferta</li>
                    <li>Ofertas H-2B y H-2A con visa patrocinada</li>
                    <li>Sincronizacion diaria con el Dept. of Labor de EE.UU.</li>
                    <li>90 dias de servicio continuo</li>
                  </ul>
                  <p className="text-xs text-base-content/50 mt-2">
                    En 90 dias puedes recibir hasta 1,200+ postulaciones enviadas a empleadores reales.
                  </p>
                </div>

                <button className="btn btn-primary w-full" onClick={handleCheckout}>
                  Pagar y activar — $160 USD
                </button>
                <p className="text-xs text-base-content/40 text-center mt-2">
                  Pago seguro con Stripe. Pago unico, no se cobra recurrentemente.
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {/* Generating drafts indicator */}
      {generatingDrafts && (
        <div className="alert bg-base-200 text-sm">
          <span className="loading loading-spinner loading-sm" />
          <span>Preparando tus primeras postulaciones de prueba...</span>
        </div>
      )}

      {/* Weekend indicator */}
      {data?.isWeekend && (
        <div className="alert bg-base-200 text-sm">
          <span>Fin de semana — las postulaciones se reanudan el lunes. Los correos enviados en fin de semana rara vez son revisados.</span>
        </div>
      )}

      {/* Draft review section */}
      {data && !data.membership.auto_send && data.drafts.length > 0 && (
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <h2 className="card-title text-xl">
              Revisa tus primeras postulaciones
              <span className="badge badge-primary">{data.drafts.length}/3</span>
            </h2>
            <p className="text-base-content/70 text-sm mb-4">
              {sender?.send_channel === 'domain'
                ? 'Preparamos estas postulaciones para que veas como postulamos por ti. Revisalas y envialas cuando estes listo.'
                : 'Creamos borradores en tu Gmail para que veas como postulamos por ti. Revisalos y envialos cuando estes listo.'}
            </p>

            <div className="space-y-3">
              {data.drafts.map((draft) => (
                <div key={draft._id} className="p-3 bg-base-200 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <p className="font-medium">{draft.job?.company} — {draft.job?.title}</p>
                      <p className="text-sm text-base-content/60 line-clamp-1">{draft.email_subject}</p>
                      {draft.job?.location && (
                        <p className="text-xs text-base-content/50">{draft.job.location}</p>
                      )}
                      {draft.job?.wage_from && (
                        <p className="text-xs text-base-content/50">
                          ${draft.job.wage_from}{draft.job.wage_to && draft.job.wage_to !== draft.job.wage_from ? `-$${draft.job.wage_to}` : ''}/{draft.job.wage_per || 'Hour'}
                        </p>
                      )}
                    </div>
                    <span className="badge badge-warning badge-sm">Borrador</span>
                  </div>
                  {draft.match_reason && (
                    <p className="text-xs text-primary/80 mt-2 italic">
                      {draft.match_reason}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {data.drafts.length >= 3 && (
              <div className="mt-4 space-y-3">
                <button
                  className="btn btn-primary w-full"
                  onClick={handleActivateAutoSend}
                  disabled={activating}
                >
                  {activating && <span className="loading loading-spinner loading-sm" />}
                  Enviar mis 3 postulaciones
                </button>
                <p className="text-xs text-base-content/50 text-center">
                  Enviaremos estos {data.drafts.length} borradores a los empleadores. Es gratis — son tus postulaciones de prueba.
                </p>
              </div>
            )}

            {data.drafts.length < 3 && (
              <p className="text-sm text-base-content/50 mt-2">
                Esperando {3 - data.drafts.length} borrador(es) mas antes de activar...
              </p>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="stat bg-base-100 rounded-box shadow">
          <div className="stat-title">Hoy</div>
          <div className="stat-value text-primary">{stats.today}</div>
          <div className="stat-desc">{stats.remaining_today} restantes</div>
        </div>
        <div className="stat bg-base-100 rounded-box shadow">
          <div className="stat-title">Pendientes</div>
          <div className="stat-value">{stats.pending}</div>
        </div>
        <div className="stat bg-base-100 rounded-box shadow">
          <div className="stat-title">Respuestas</div>
          <div className="stat-value text-success">{stats.replied}</div>
        </div>
        <div className="stat bg-base-100 rounded-box shadow">
          <div className="stat-title">Entrevistas</div>
          <div className="stat-value text-accent">{stats.interviews}</div>
        </div>
        <div className="stat bg-base-100 rounded-box shadow">
          <div className="stat-title">Borradores</div>
          <div className="stat-value text-warning">{stats.drafts}</div>
        </div>
      </div>

      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title">Postulaciones recientes</h2>
          {!data?.recentApplications ||
          data.recentApplications.length === 0 ? (
            <p className="text-base-content/50">
              Aun no hay postulaciones enviadas. {!data?.membership.auto_send
                ? 'Revisa los borradores de prueba y envialos para comenzar.'
                : 'El sistema enviara postulaciones automaticamente en el proximo ciclo.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Asunto</th>
                    <th>Estado</th>
                    <th>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentApplications.map((app) => (
                    <tr key={app._id}>
                      <td>
                        <div>{app.email_subject}</div>
                        {app.match_reason && (
                          <div className="text-xs text-primary/70 italic mt-0.5">{app.match_reason}</div>
                        )}
                      </td>
                      <td>
                        <span className={`badge badge-sm ${STATUS_BADGES[app.status] || ''}`}>{app.status}</span>
                      </td>
                      <td className="text-xs">
                        {new Date(app.created_at).toLocaleString('es-CO')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
